// src/components/Sidebar.js
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { chartMap } from '../components/ChartMap';
import { List, ListItemButton, ListItemIcon, ListItemText, Box, Collapse, } from '@mui/material';
import { Home, Receipt, ReceiptLong, RequestQuote, SupervisorAccount, Inventory2, BarChart, Settings, ExpandMore, AddBusiness } from '@mui/icons-material';

const Sidebar = ({ hasLimit }) => {
  const location = useLocation();
  const [openMap, setOpenMap] = React.useState({});
  const chartChildren = Object.entries(chartMap).map(([key, { label, slug }]) => ({
    label,
    path: `/chart/${slug}`, // 小文字ハイフンのURL
  }));

  const handleToggle = (key) => {
    setOpenMap(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  //選択判定
  const isExactSelected = (path) => location.pathname === path;
  const isChildSelected = (children = []) =>
    children.some(child => location.pathname.startsWith(child.path));

  //メニュー定義（親子構造）
  const menu = [
    {
      label: 'ダッシュボード',
      icon: Home,
      path: '/',
    },
    {
      label: 'データ分析',
      icon: BarChart,
      children: [
        { label: 'すべて', path: '/chart', limit: 'data_analysis' },
        ...chartChildren.map(child => ({
          ...child,
          limit: 'data_analysis',
        })),
      ],
    },
    {
      label: '見積管理',
      icon: Receipt,
      children: [
        { label: '見積一覧', path: '/estimates', limit: 'max_estimates' },
        { label: '新規見積作成', path: '/estimates/new', limit: 'max_estimates' },
      ],
    },
    {
      label: '注文管理',
      icon: ReceiptLong,
      children: [
        { label: '注文一覧', path: '/orders' },
        { label: '新規注文作成', path: '/orders/new' },
        { label: 'ピッキング処理', path: '/picking' },
        { label: '注文完了処理', path: '/orders/complete' },
      ],
    },
    {
      label: '請求管理',
      icon: RequestQuote,
      children: [
        { label: '請求一覧', path: '/invoices' },
        /*{ label: '請求状況（注文単位）', path: '/sales/billing' },*/
        { label: '請求作成', path: '/invoices/create' },
      ],
    },
    {
      label: '商品・在庫管理',
      icon: Inventory2,
      children: [
        { label: '商品在庫一覧', path: '/products' },
        { label: '商品の新規登録', path: '/products/new' },
        { label: '入出庫登録', path: '/inventory/transactions/register' },
        { label: '入出庫履歴', path: '/inventory/transactions' },
        /*{ label: '仕入商品在庫一覧', path: '/items' },
        { label: '仕入商品の入出庫登録', path: '/items/transactions/register' },
        { label: '仕入商品の入出庫履歴', path: '/items/transactions' },*/
      ],
    },
    {
      label: '発注管理',
      icon: AddBusiness,
      children: [
        { label: '発注一覧（発注履歴）', path: '/purchases', limit: 'max_purchase' },
        { label: '発注書作成', path: '/purchases/new', limit: 'max_purchase' },
        { label: '仕入れ先管理', path: '/suppliers' },
      ],
    },
    {
      label: '取引先管理',
      icon: SupervisorAccount,
      children: [
        { label: '取引先一覧', path: '/clients', limit: 'max_clients', },
        { label: '新規取引先登録', path: '/clients/new', limit: 'max_clients', },
      ],
    },
    {
      label: '設定',
      icon: Settings,
      children: [
        { label: '基本設定', path: '/setting' },
        { label: '会社設定', path: '/setting/invoice', limit: 'invoices_and_delivery', },
        { label: 'ECサイト設定', path: '/setting/company', limit: 'ec', },
        { label: '倉庫管理', path: '/setting/warehouse' },
        { label: '商品カテゴリー管理', path: '/setting/category', },
        { label: 'ピッキング端末管理', path: '/setting/picking-device', limit: 'max_picking_devices', },
        { label: 'ユーザ管理', path: '/setting/users' },
        //{ label: '外部連携', path: '/integrations' },
      ],
    },
  ];

  return (
    <>
      {/* ロゴ */}
      <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
        <Box
          component="img"
          src={`${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_LOGO}`}
          alt="Tendenec"
          sx={{ maxWidth: '100%', maxHeight: 100 }}
        />
      </Box>

      <List sx={{ px: 1 }}>
        {menu.map((item) => {
          const isDisabled = item.limit ? !hasLimit(item.limit) : false;
          const Icon = item.icon;
          const routeOpen = isChildSelected(item.children);
          const open = openMap[item.label] ?? routeOpen;
          const selected = item.path ? isExactSelected(item.path) : routeOpen;

          // 単体メニュー
          if (!item.children) {
            return (
              <ListItemButton
                key={item.label}
                component={RouterLink}
                to={isDisabled ? '#' : item.path}
                selected={selected}
                disabled={isDisabled}
              >
                <ListItemIcon><Icon /></ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            );
          }

          // 子メニューあり
          return (
            <React.Fragment key={item.label}>
              <ListItemButton
                onClick={() => handleToggle(item.label)}
                selected={selected}
              >
                <ListItemIcon><Icon /></ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                <ExpandMore
                  sx={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: '0.2s',
                  }}
                />
              </ListItemButton>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => {
                    const childDisabled = child.limit ? !hasLimit(child.limit) : false;
                    const ChildIcon = child.icon;
                    return (
                      <ListItemButton
                        key={child.path}
                        component={childDisabled ? 'div' : RouterLink}
                        to={childDisabled ? undefined : child.path}
                        selected={isExactSelected(child.path)}
                        sx={{ pl: 6 }}
                        disabled={childDisabled}
                      >
                        {ChildIcon && (
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ChildIcon fontSize="small" />
                          </ListItemIcon>
                        )}
                        <ListItemText primary={child.label} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};

export default Sidebar;
